import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardActionArea,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import api from '../../services/api';
import { toImageUrl } from '../../utils/media';

function imageSrc(value) {
  return toImageUrl(value) || '/logo.png';
}

function formatPrice(value) {
  if (value === null || value === undefined || value === '') return 'Request pricing';
  const n = Number(value);
  if (!Number.isFinite(n)) return 'Request pricing';
  return `$${n.toFixed(2)}`;
}

function CategoryCard({ category, onSelect }) {
  return (
    <Card sx={{ borderRadius: 5, overflow: 'hidden', border: '1px solid rgba(148,112,59,.25)',
      boxShadow: '0 16px 38px rgba(20,33,22,.08)', bgcolor: '#fff' }}>
      <CardActionArea onClick={() => onSelect(category.slug)} aria-label={`Explore ${category.nombre}`}
        sx={{ position: 'relative', display: 'block', width: '100%', aspectRatio: '1 / 1',
          '&:hover .category-photo': { transform: 'scale(1.04)' },
          '&.Mui-focusVisible': { outline: '3px solid #d8b16a', outlineOffset: -3 } }}>
        <Box component="img" className="category-photo" src={imageSrc(category.imagen_url)} alt=""
          sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%',
            objectFit: 'cover', transition: 'transform .4s ease',
            '@media (prefers-reduced-motion: reduce)': { transition: 'none' } }} />
        <Box sx={{ position: 'absolute', inset: 0,
          background: 'linear-gradient(180deg, transparent 35%, rgba(11,44,28,.5) 65%, #123b27 100%)' }} />
        <Stack spacing={1.5} sx={{ position: 'absolute', bottom: 0, left: 0, right: 0,
          p: { xs: 2.5, lg: 3 }, color: '#fff', textAlign: 'center' }}>
          <Typography component="h3" sx={{ fontFamily: 'Georgia, serif', fontSize: { xs: 28, lg: 34 },
            fontWeight: 700, lineHeight: 1.1, textTransform: 'uppercase', overflowWrap: 'anywhere' }}>
            {category.nombre}
          </Typography>
          <Box component="span" sx={{ border: '1px solid rgba(255,255,255,.9)', borderRadius: 999,
            py: 1.15, px: 1, fontFamily: 'Georgia, serif', fontSize: 15, fontWeight: 700 }}>
            Explore varieties ↗
          </Box>
        </Stack>
      </CardActionArea>
    </Card>
  );
}

function ProductCard({ item, onDetail }) {
  const square = item.categoria_formato_imagen === 'cuadrada';
  return (
    <Card
      sx={{
        position: 'relative',
        width: '100%',
        height: square ? 'auto' : { xs: 520, sm: 540, md: 560 },
        aspectRatio: square ? '1 / 1' : 'auto',
        borderRadius: 5,
        overflow: 'hidden',
        bgcolor: '#fff',
        border: '1px solid rgba(20,33,22,.08)',
        boxShadow: '0 18px 45px rgba(20,33,22,.10)',
        cursor: 'pointer',
        transition: 'transform .26s ease, box-shadow .26s ease',
        '&:hover': {
          transform: 'translateY(-8px)',
          boxShadow: '0 28px 68px rgba(20,33,22,.18)'
        },
        '&:hover .product-image': {
          transform: 'scale(1.08)'
        },
        '&:hover .product-button': {
          bgcolor: '#fff',
          color: '#102d1d'
        }
      }}
      onClick={() => onDetail(item)}
    >
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          bgcolor: '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: { xs: 2, md: 2.5 }
        }}
      >
        <Box
          component="img"
          className="product-image"
          src={imageSrc(item.imagen_principal)}
          alt={item.nombre || 'Product'}
          sx={{
            width: '100%',
            height: '100%',
            objectFit: square ? 'contain' : 'cover',
            objectPosition: 'center',
            display: 'block',
            transition: 'transform .65s ease'
          }}
        />
      </Box>

      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          background:
            'linear-gradient(to bottom, rgba(8,18,12,0) 36%, rgba(8,18,12,.26) 60%, rgba(8,18,12,.92) 100%)'
        }}
      />

      <Box
        sx={{
          position: 'absolute',
          top: 18,
          left: 18
        }}
      >
        {!!item.categoria_nombre && (
          <Chip
            label={item.categoria_nombre}
            size="small"
            sx={{
              bgcolor: 'rgba(255,255,255,.90)',
              color: '#7b5b31',
              fontWeight: 900,
              textTransform: 'uppercase',
              letterSpacing: 0.6,
              backdropFilter: 'blur(10px)'
            }}
          />
        )}
      </Box>

      <Box
        sx={{
          position: 'absolute',
          left: 0,
          right: 0,
          bottom: 0,
          p: square ? 2 : { xs: 2.5, md: 3 },
          color: '#fff'
        }}
      >
        <Typography
          sx={{
            fontWeight: 900,
            fontSize: square ? { xs: 24, md: 26 } : { xs: 28, md: 34 },
            lineHeight: 1,
            letterSpacing: square ? 1.5 : 4,
            overflowWrap: 'anywhere',
            textTransform: 'uppercase',
            textShadow: '0 8px 24px rgba(0,0,0,.35)'
          }}
        >
          {item.nombre || 'Product'}
        </Typography>

        <Typography
          sx={{
            mt: square ? 0.8 : 1.4,
            fontWeight: 900,
            fontSize: square ? 15 : 18,
            color: 'rgba(255,255,255,.92)',
            textShadow: '0 8px 20px rgba(0,0,0,.35)'
          }}
        >
          {formatPrice(item.precio)}
        </Typography>

        <Button
          className="product-button"
          fullWidth
          variant="outlined"
          onClick={(e) => {
            e.stopPropagation();
            onDetail(item);
          }}
          sx={{
            mt: square ? 1.2 : 2.2,
            borderRadius: 999,
            py: square ? 0.8 : 1.15,
            fontWeight: 900,
            textTransform: 'none',
            color: '#fff',
            borderColor: 'rgba(255,255,255,.80)',
            bgcolor: 'rgba(255,255,255,.08)',
            backdropFilter: 'blur(10px)',
            '&:hover': {
              bgcolor: '#fff',
              color: '#102d1d',
              borderColor: '#fff'
            }
          }}
        >
          View details
        </Button>
      </Box>
    </Card>
  );
}

function ProductDetailModal({ open, loading, producto, selectedImage, setSelectedImage, onClose }) {
  const galeria = useMemo(() => {
    if (!producto) return [];

    const extra = Array.isArray(producto.galeria)
      ? producto.galeria.map((x) => x.imagen_url).filter(Boolean)
      : [];

    const all = [producto.imagen_principal, ...extra].filter(Boolean);
    return [...new Set(all)];
  }, [producto]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: { xs: 0, sm: 5 },
          overflow: 'hidden'
        }
      }}
    >
      <DialogTitle
        sx={{
          px: { xs: 2.5, md: 4 },
          py: 2.2,
          borderBottom: '1px solid rgba(20,33,22,.08)'
        }}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
          <Box>
            <Typography
              sx={{
                fontWeight: 900,
                color: '#142116',
                fontSize: { xs: 22, md: 28 },
                lineHeight: 1.1,
                textTransform: 'uppercase',
                letterSpacing: 1.2
              }}
            >
              {producto?.nombre || 'Product details'}
            </Typography>

            {!!producto?.categoria_nombre && (
              <Typography
                sx={{
                  mt: 0.6,
                  color: '#7b5b31',
                  fontWeight: 800,
                  fontSize: 13,
                  textTransform: 'uppercase',
                  letterSpacing: 1.4
                }}
              >
                {producto.categoria_nombre}
              </Typography>
            )}
          </Box>

          <Button
            onClick={onClose}
            aria-label="Close"
            sx={{
              minWidth: 44,
              width: 44,
              height: 44,
              borderRadius: '50%',
              color: '#142116',
              bgcolor: 'rgba(20,33,22,.06)',
              fontSize: 24,
              fontWeight: 900,
              lineHeight: 1,
              p: 0,
              '&:hover': {
                bgcolor: 'rgba(20,33,22,.12)'
              }
            }}
          >
            ×
          </Button>
        </Stack>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        {loading ? (
          <Box sx={{ py: 10, display: 'flex', justifyContent: 'center' }}>
            <CircularProgress />
          </Box>
        ) : !producto ? (
          <Box sx={{ py: 8, px: 3, textAlign: 'center' }}>
            <Typography sx={{ fontWeight: 800 }}>Product not found</Typography>
          </Box>
        ) : (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1.1fr .9fr' }
            }}
          >
            <Box
              sx={{
                p: { xs: 2.5, md: 4 },
                bgcolor: '#faf9f6',
                borderRight: { md: '1px solid rgba(20,33,22,.08)' }
              }}
            >
              <Box
                sx={{
                  height: { xs: 340, md: 520 },
                  bgcolor: '#fff',
                  borderRadius: 4,
                  border: '1px solid rgba(20,33,22,.08)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  p: { xs: 2, md: 4 },
                  overflow: 'hidden'
                }}
              >
                <Box
                  component="img"
                  src={imageSrc(selectedImage || producto.imagen_principal)}
                  alt={producto.nombre}
                  sx={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain',
                    objectPosition: 'center',
                    display: 'block'
                  }}
                />
              </Box>

              {!!galeria.length && (
                <Stack
                  direction="row"
                  spacing={1.2}
                  sx={{
                    mt: 2,
                    flexWrap: 'wrap',
                    rowGap: 1.2
                  }}
                >
                  {galeria.map((img, idx) => {
                    const selected = selectedImage === img;

                    return (
                      <Box
                        key={`${img}-${idx}`}
                        component="button"
                        type="button"
                        onClick={() => setSelectedImage(img)}
                        sx={{
                          width: 78,
                          height: 78,
                          borderRadius: 2,
                          overflow: 'hidden',
                          p: 0.7,
                          cursor: 'pointer',
                          bgcolor: '#fff',
                          border: selected ? '2px solid #183f2a' : '1px solid rgba(20,33,22,.16)'
                        }}
                      >
                        <Box
                          component="img"
                          src={imageSrc(img)}
                          alt={`Imagen ${idx + 1}`}
                          sx={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'contain',
                            display: 'block'
                          }}
                        />
                      </Box>
                    );
                  })}
                </Stack>
              )}
            </Box>

            <Box sx={{ p: { xs: 2.5, md: 4 } }}>
              <Chip
                label="Premium selection"
                sx={{
                  mb: 2.5,
                  bgcolor: '#f2ece8',
                  color: '#7b5b31',
                  fontWeight: 900,
                  letterSpacing: 0.5
                }}
              />

              <Typography
                sx={{
                  fontWeight: 900,
                  color: '#142116',
                  fontSize: { xs: 34, md: 48 },
                  lineHeight: 1,
                  letterSpacing: -1,
                  textTransform: 'uppercase',
                  mb: 2
                }}
              >
                {producto.nombre}
              </Typography>

              <Typography
                sx={{
                  fontWeight: 900,
                  color: '#183f2a',
                  fontSize: 24,
                  mb: 3
                }}
              >
                {formatPrice(producto.precio)}
              </Typography>

              <Divider sx={{ mb: 3 }} />

              <Typography
                sx={{
                  color: 'text.secondary',
                  lineHeight: 1.9,
                  fontSize: 16
                }}
              >
                {producto.descripcion || 'Contact us for details about this flower variety.'}
              </Typography>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mt: 4 }}>
                <Button
                  variant="contained"
                  onClick={onClose}
                  sx={{
                    borderRadius: 999,
                    px: 3,
                    py: 1.2,
                    bgcolor: '#183f2a',
                    fontWeight: 900,
                    textTransform: 'none',
                    '&:hover': { bgcolor: '#102d1d' }
                  }}
                >
                  Continue browsing
                </Button>
              </Stack>
            </Box>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function PublicCatalogo() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);

  const [categorias, setCategorias] = useState([]);
  const [productos, setProductos] = useState([]);

  const [q, setQ] = useState(searchParams.get('q') || '');

  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedImage, setSelectedImage] = useState('');

  const categoria = searchParams.get('categoria') || '';
  const qParam = searchParams.get('q') || '';
  const showCategories = !categoria && !qParam;

  useEffect(() => {
    setQ(qParam);
  }, [qParam]);

  const loadData = useCallback(async () => {
    setLoading(true);

    try {
      const [{ data: catData }, { data: prodData }] = await Promise.all([
        api.get('/api/web/categorias'),
        showCategories ? Promise.resolve({ data: [] }) : api.get('/api/web/productos', {
          params: {
            categoria: categoria || undefined,
            q: qParam || undefined,
            limit: 100
          }
        })
      ]);

      setCategorias(Array.isArray(catData) ? catData : []);
      setProductos(Array.isArray(prodData) ? prodData : []);
    } catch (error) {
      console.error('Error cargando catálogo', error);
    } finally {
      setLoading(false);
    }
  }, [categoria, qParam, showCategories]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleBuscar = () => {
    const next = {};

    if (categoria) next.categoria = categoria;
    if (q.trim()) next.q = q.trim();

    setSearchParams(next);
  };

  const handleClearSearch = () => {
    const next = {};

    if (categoria) next.categoria = categoria;

    setQ('');
    setSearchParams(next);
  };

  const handleCategory = (catSlug = '') => {
    const next = {};

    if (catSlug && qParam) next.q = qParam;
    if (catSlug) next.categoria = catSlug;
    if (!catSlug) setQ('');

    setSearchParams(next);
  };

  const openProductDetail = async (item) => {
    setDetailOpen(true);
    setDetailLoading(true);
    setSelectedProduct(null);
    setSelectedImage('');

    try {
      const { data } = await api.get(`/api/web/productos/${item.slug}`);

      setSelectedProduct(data || null);
      setSelectedImage(
        data?.imagen_principal || data?.galeria?.[0]?.imagen_url || item.imagen_principal || ''
      );
    } catch (error) {
      console.error('Error cargando detalle del producto', error);
      setSelectedProduct(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const closeProductDetail = () => {
    setDetailOpen(false);
    setSelectedProduct(null);
    setSelectedImage('');
    setDetailLoading(false);
  };

  return (
    <Box sx={{ bgcolor: '#f8f6f4', minHeight: '100vh' }}>
      <Container maxWidth="xl" sx={{ py: { xs: 5, md: 7 } }}>
        <Box component="header" sx={{ mb: 3.5 }}>
          <Typography sx={{ fontSize: 12, fontWeight: 800, letterSpacing: 4,
            color: '#94703b', textTransform: 'uppercase', mb: 1.2 }}>
            Grown in Ecuador
          </Typography>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={{ xs: 2, md: 4 }}
            alignItems={{ xs: 'flex-start', md: 'center' }} sx={{ mb: { xs: 3, md: 4 } }}>
            <Typography component="h1" sx={{ fontFamily: 'Georgia, serif',
              fontSize: { xs: 40, sm: 52, md: 62 }, fontWeight: 500, letterSpacing: -1.8,
              lineHeight: 1.08, color: '#183f2a' }}>
              Flowers that inspire<Box component="span" sx={{ color: '#94703b' }}>.</Box>
            </Typography>
            <Typography sx={{ maxWidth: 480, pl: { md: 4 },
              borderLeft: { md: '1px solid rgba(148,112,59,.35)' },
              color: '#6b6e71', fontSize: { xs: 16, md: 18 }, lineHeight: 1.6 }}>
              Discover our varieties and find the perfect flowers for your next order.
            </Typography>
          </Stack>

          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}
            justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'center' }}
            sx={{ pb: 2.2, borderBottom: '1px solid rgba(148,112,59,.4)' }}>
            <Stack direction="row" spacing={1.2} sx={{ flexWrap: 'wrap', rowGap: 1,
              '& .MuiChip-root': { height: 42, px: 1.5, borderRadius: 999,
                fontFamily: 'Georgia, serif', fontSize: 15, fontWeight: 700 },
              '& .MuiChip-root:focus-visible': { outline: '2px solid #94703b', outlineOffset: 3 }
            }}>
              <Chip label="All" clickable onClick={() => handleCategory('')}
                aria-pressed={!categoria}
                sx={{ bgcolor: !categoria ? '#183f2a' : '#efebe6', color: !categoria ? '#fff' : '#183f2a',
                  '&:hover': { bgcolor: !categoria ? '#102d1d' : '#e5dfd7' } }} />
              {categorias.map((cat) => {
                const active = categoria === cat.slug;
                return (
                  <Chip key={cat.id} label={cat.nombre} clickable onClick={() => handleCategory(cat.slug)}
                    aria-pressed={active}
                    sx={{ bgcolor: active ? '#183f2a' : '#efebe6', color: active ? '#fff' : '#183f2a',
                      '&:hover': { bgcolor: active ? '#102d1d' : '#e5dfd7' } }} />
                );
              })}
            </Stack>
            <Stack component="form" direction="row" spacing={1} alignItems="center"
              onSubmit={(event) => { event.preventDefault(); handleBuscar(); }}
              sx={{ width: { xs: '100%', md: 480 }, flexShrink: 0 }}>
              <TextField fullWidth size="small" placeholder="Search varieties…" value={q}
                onChange={(event) => setQ(event.target.value)}
                inputProps={{ 'aria-label': 'Search varieties' }}
                sx={{ bgcolor: '#fff', borderRadius: 999,
                  '& .MuiOutlinedInput-root': { borderRadius: 999, height: 44 },
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#d9d0c5' },
                  '& input': { height: 'auto', maxWidth: 'none' },
                  '& .Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#94703b' }
                }} />
              <Button type="submit" variant="contained" sx={{ px: 2.5, height: 44,
                borderRadius: 999, bgcolor: '#183f2a', boxShadow: 'none', fontWeight: 700,
                textTransform: 'none', '&:hover': { bgcolor: '#102d1d', boxShadow: 'none' } }}>Search</Button>
              {!!qParam && (
                <Button onClick={handleClearSearch} sx={{ color: '#795b29', borderRadius: 999,
                  textTransform: 'none', fontWeight: 700 }}>Clear</Button>
              )}
            </Stack>
          </Stack>
        </Box>

        {loading ? (
          <Box sx={{ py: 10, display: 'flex', justifyContent: 'center' }}>
            <CircularProgress />
          </Box>
        ) : showCategories ? (
          <Box component="section" aria-labelledby="catalog-categories-title">
            <Typography component="h2" id="catalog-categories-title" sx={{ mb: 2.5, fontSize: 12,
              fontWeight: 800, letterSpacing: 3, color: '#94703b' }}>EXPLORE OUR CATEGORIES</Typography>
            {categorias.length ? (
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))',
                md: 'repeat(3, minmax(0, 1fr))' }, gap: { xs: 2.5, md: 4 } }}>
                {categorias.map((category) => <CategoryCard key={category.id} category={category} onSelect={handleCategory} />)}
              </Box>
            ) : <Typography color="text.secondary">Our flower categories will be available here soon.</Typography>}
          </Box>
        ) : productos.length ? (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, minmax(0, 1fr))',
                md: 'repeat(3, minmax(0, 1fr))',
                lg: 'repeat(4, minmax(0, 1fr))'
              },
              gap: 3,
              alignItems: 'stretch'
            }}
          >
            {productos.map((item) => (
              <Box key={item.id} sx={{ minWidth: 0 }}>
                <ProductCard item={item} onDetail={openProductDetail} />
              </Box>
            ))}
          </Box>
        ) : (
          <Box
            sx={{
              py: 8,
              px: 3,
              textAlign: 'center',
              color: 'text.secondary',
              bgcolor: '#fff',
              borderRadius: 4,
              border: '1px dashed rgba(20,33,22,.18)'
            }}
          >
            <Typography sx={{ fontWeight: 900, color: '#142116', mb: 1 }}>
              No products available.
            </Typography>
            <Typography>Try another category or change your search.</Typography>
          </Box>
        )}
      </Container>

      <ProductDetailModal
        open={detailOpen}
        loading={detailLoading}
        producto={selectedProduct}
        selectedImage={selectedImage}
        setSelectedImage={setSelectedImage}
        onClose={closeProductDetail}
      />
    </Box>
  );
}
